import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SchemeHeader } from '../../components/Scheme/SchemeHeader';
import { CustomDropdown } from '../../components/Common/CustomDropdown';

export const AssignScheme: React.FC = () => {
  const navigate = useNavigate();
  const [selectedScheme, setSelectedScheme] = useState('Milk Scheme (SCH-2023-089)');
  const [selectedZone, setSelectedZone] = useState('Mumbai');
  const [selectedDistributorType, setSelectedDistributorType] = useState('All Types (GT & MT)');
  const [searchDistributor, setSearchDistributor] = useState('');
  const [selectedDistributorIds, setSelectedDistributorIds] = useState<string[]>(['D-101', 'D-102']);

  const distributorsList = [
    { id: 'D-101', name: 'Krishna Traders', code: 'DIST-9928', city: 'Vashi/ Mumbai', type: 'GT', status: 'Active' },
    { id: 'D-102', name: 'Metro Supermarket Depot', code: 'DIST-9929', city: 'Vasai/ Mumbai', type: 'MT', status: 'Active' },
    { id: 'D-103', name: 'Om Sai Enterprises', code: 'DIST-9930', city: 'Thane', type: 'GT', status: 'Active' },
    { id: 'D-104', name: 'Shree Dairy Distributors', code: 'DIST-9931', city: 'Andheri/ Mumbai', type: 'GT', status: 'Active' },
    { id: 'D-105', name: 'Reliance Retail Logistics', code: 'DIST-9932', city: 'Navi Mumbai', type: 'MT', status: 'Active' },
  ];

  const toggleSelectDistributor = (id: string) => {
    setSelectedDistributorIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAssign = () => {
    alert(`Successfully assigned Scheme (${selectedScheme}) to ${selectedDistributorIds.length} distributors!`);
    navigate('/scheme');
  };

  return (
    <div className="min-h-screen">
      <SchemeHeader
        title="Assign Scheme"
        showBack={true}
        onBackClick={() => navigate('/scheme')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side: Select Scheme & Assignment Target Configuration */}
        <div className="lg:col-span-1 bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm space-y-5">
          <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3">
            Assignment Parameters
          </h2>

          {/* Select Scheme */}
          <div>
            <CustomDropdown
              label="Select Scheme to Assign *"
              placeholder="Select Scheme"
              options={[
                'Milk Scheme (SCH-2023-089)',
                'Curd Bonanza Scheme (SCH-2023-090)',
                'Butter Bulk Offer (SCH-2023-091)',
              ]}
              value={selectedScheme}
              onChange={(val: string) => setSelectedScheme(val)}
              searchable={true}
            />
          </div>

          {/* Filter Zone/City */}
          <div>
            <CustomDropdown
              label="Filter Zone / City"
              placeholder="Select City"
              options={['All Cities', 'Mumbai', 'Pune']}
              value={selectedZone}
              onChange={(val: string) => setSelectedZone(val)}
              searchable={false}
            />
          </div>

          {/* Distributor Type */}
          <div>
            <CustomDropdown
              label="Distributor Type"
              placeholder="Select Type"
              options={['All Types (GT & MT)', 'General Trade (GT)', 'Modern Trade (MT)']}
              value={selectedDistributorType}
              onChange={(val: string) => setSelectedDistributorType(val)}
              searchable={false}
            />
          </div>

          <button
            onClick={handleAssign}
            disabled={selectedDistributorIds.length === 0}
            className="w-full bg-[#B71C1C] hover:bg-[#960101] text-white text-xs md:text-sm font-semibold py-3 rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-50"
          >
            Confirm & Assign Scheme ({selectedDistributorIds.length})
          </button>
        </div>

        {/* Right Side: Select Distributors Table */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-base font-bold text-gray-900">
              Select Distributors ({selectedDistributorIds.length} Selected)
            </h2>
            <input
              type="text"
              placeholder="Search distributor or code..."
              value={searchDistributor}
              onChange={(e) => setSearchDistributor(e.target.value)}
              className="bg-white border border-gray-300 focus:border-[#B71C1C] outline-none text-xs rounded-xl py-1.5 px-3 w-60"
            />
          </div>

          <div className="overflow-x-auto border border-gray-100 rounded-xl">
            <table className="w-full text-left text-xs md:text-sm">
              <thead className="bg-[#FFF9E6] text-gray-600 font-semibold">
                <tr>
                  <th className="py-3 px-4 w-10 text-center">Select</th>
                  <th className="py-3 px-4">Distributor Code</th>
                  <th className="py-3 px-4">Distributor Name</th>
                  <th className="py-3 px-4">City/Region</th>
                  <th className="py-3 px-4">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {distributorsList.map((dist) => {
                  const isChecked = selectedDistributorIds.includes(dist.id);
                  return (
                    <tr
                      key={dist.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        isChecked ? 'bg-red-50/20' : ''
                      }`}
                    >
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelectDistributor(dist.id)}
                          className="rounded border-gray-300 text-[#B71C1C] focus:ring-[#B71C1C] cursor-pointer"
                        />
                      </td>
                      <td className="py-3 px-4 font-mono text-gray-500 font-medium">{dist.code}</td>
                      <td className="py-3 px-4 font-bold text-gray-800">{dist.name}</td>
                      <td className="py-3 px-4 text-gray-600">{dist.city}</td>
                      <td className="py-3 px-4 font-semibold text-gray-700">{dist.type}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
